import {
	Alert,
	Autocomplete,
	Box,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	IconButton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { useState } from "react";
import { useMissionControlPasswordsGeneratorViewModel } from "./MissionControlPasswordsGenerator.vm";
import type { PasswordResult } from "./MissionControlPasswordsGenerator.types";

function PasswordCard({ result }: { result: PasswordResult }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		navigator.clipboard.writeText(result.password);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				p: 1.5,
				borderRadius: 1,
				bgcolor: "grey.50",
			}}
		>
			<Box>
				<Typography variant="body2" color="text.secondary">
					{result.label}
				</Typography>
				<Typography variant="h4" sx={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: 4 }}>
					{result.password}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					{result.validPeriod}
				</Typography>
			</Box>
			<Tooltip title={copied ? "Copied!" : "Copy password"}>
				<IconButton onClick={handleCopy} size="small">
					<ContentCopy fontSize="small" />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

export const MissionControlPasswordsGenerator: React.FC = () => {
	const vm = useMissionControlPasswordsGeneratorViewModel();

	if (!vm.isSuperUser) {
		return (
			<Box sx={{ p: 4, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				p: { xs: 2, sm: 4 },
				maxWidth: "md",
				margin: "auto",
				width: "100%",
			}}
		>
			<Typography variant="h5" gutterBottom>
				Mission Control Passwords
			</Typography>

			{/* Weekly Password — always visible */}
			<Card variant="outlined" sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="subtitle2" gutterBottom>
						Weekly Password (not operator-specific)
					</Typography>
					{vm.weeklyPassword && <PasswordCard result={vm.weeklyPassword} />}
				</CardContent>
			</Card>

			{/* Operator-specific passwords */}
			<Card variant="outlined">
				<CardContent>
					<Stack spacing={3}>
						<Typography variant="subtitle2">Operator-Specific Passwords</Typography>

						<Autocomplete
							options={vm.teamMembers}
							getOptionLabel={(option) => (option.name ? `${option.name} (ID: ${option.appUserId})` : "Unknown")}
							value={vm.selectedUser}
							onChange={(_, newValue) => vm.setSelectedUser(newValue)}
							loading={vm.loadingTeamMembers}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Select Operator"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{vm.loadingTeamMembers ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							disabled={vm.loadingTeamMembers}
						/>

						{vm.error && <Alert severity="error">{vm.error}</Alert>}

						{vm.passwords.length > 0 && (
							<>
								<Divider />
								<Stack spacing={1.5}>
									{vm.passwords.map((pw) => (
										<PasswordCard key={pw.label} result={pw} />
									))}
								</Stack>
							</>
						)}
					</Stack>
				</CardContent>
			</Card>
		</Box>
	);
};
